import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { CARTO_DARK_MATTER } from "./map/basemap/carto-dark-matter";
import { readTheme, writeTheme, nextTheme, type Theme } from "./lib/theme";
import { DEFAULT_VIEW, readQuery, writeQuery } from "./lib/router";
import { MapStore } from "./lib/map-store";
import { COPY } from "./content/copy";
import { LAYER_REGISTRY } from "./map/layers/registry";
import { loadGeometry } from "./map/layers/geometry-loader";
import { ChoroplethLayer } from "./map/layers/choropleth-layer";
import type { LayerName, MeasureId } from "./map/layers/types";
import { LayerTogglePanel } from "./components/LayerTogglePanel";
import { LegendStrip } from "./components/LegendStrip";
import { supabase } from "./lib/supabase";

export function MapView() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [theme, setTheme] = useState<Theme>(readTheme());
  const [activeLayer, setActiveLayerState] = useState<LayerName | null>(null);
  const [activeMeasure, setActiveMeasureState] = useState<MeasureId | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const q = readQuery();
    const domTheme = readTheme();
    const resolvedTheme = q.theme ?? domTheme;
    if (q.theme !== undefined && q.theme !== domTheme) {
      writeTheme(q.theme);
      setTheme(q.theme);
    } else if (resolvedTheme !== domTheme) {
      // no URL, no override: DOM is authoritative
      setTheme(domTheme);
    }
    const initial = { ...DEFAULT_VIEW, ...q, theme: resolvedTheme };

    MapStore.setView(initial);

    const map = L.map(containerRef.current, {
      center: initial.center,
      zoom: initial.zoom,
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
    });
    mapRef.current = map;

    L.tileLayer(CARTO_DARK_MATTER.urlTemplate, {
      subdomains: CARTO_DARK_MATTER.subdomains,
      attribution: COPY.chrome.attribution.text,
      maxZoom: CARTO_DARK_MATTER.maxZoom,
      detectRetina: CARTO_DARK_MATTER.detectRetina,
    }).addTo(map);

    const persist = () => {
      const c = map.getCenter();
      writeQuery({ center: [c.lat, c.lng], zoom: map.getZoom() }, true);
    };
    map.on("moveend", persist);
    map.on("zoomend", persist);

    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Hydrate active layer/measure from the URL once.
  useEffect(() => {
    const q = readQuery();
    const layer = q.layers?.[0] ?? null;
    setActiveLayerState(layer);
    if (layer) {
      const measure = q.measures?.[layer] ?? LAYER_REGISTRY[layer].defaultMeasure;
      setActiveMeasureState(measure);
      MapStore.setActiveLayer(layer);
      MapStore.setMeasure(layer, measure);
    }
  }, []);

  // Mount/unmount the choropleth canvas when the active layer or measure changes.
  useEffect(() => {
    if (!activeLayer || !activeMeasure || !mapRef.current) return;
    let layer: ChoroplethLayer | undefined;
    let cancelled = false;
    (async () => {
      const geometry = await loadGeometry("montreal");
      const { data, error } = await supabase().rpc("get_da_measure", { p_measure: activeMeasure });
      if (error || cancelled || !mapRef.current) return;
      const values = new Map(
        (data as { da_uid: string; value: number | null; bin_index: number | null }[]).map(
          (r) => [r.da_uid, { value: r.value, binIndex: r.bin_index }],
        ),
      );
      layer = new ChoroplethLayer({
        map: mapRef.current,
        region: "montreal",
        layer: activeLayer,
        measure: activeMeasure,
        geometry,
        values,
      });
      await layer.mount();
    })();
    return () => {
      cancelled = true;
      layer?.unmount();
    };
  }, [activeLayer, activeMeasure, theme]);

  const onToggleLayer = (name: LayerName | null) => {
    setActiveLayerState(name);
    const measure = name ? LAYER_REGISTRY[name].defaultMeasure : null;
    setActiveMeasureState(measure);
    MapStore.setActiveLayer(name);
    if (name && measure) MapStore.setMeasure(name, measure);
    writeQuery({
      layers: name ? [name] : [],
      measures: name && measure ? { [name]: measure } : {},
    });
  };

  const onChangeMeasure = (m: MeasureId) => {
    if (!activeLayer) return;
    setActiveMeasureState(m);
    MapStore.setMeasure(activeLayer, m);
    writeQuery({ measures: { [activeLayer]: m } });
  };

  const onToggleTheme = () => {
    const next = nextTheme(theme);
    setTheme(next);
    writeTheme(next);
    writeQuery({ theme: next }, true);
  };

  return (
    <div className="map-root">
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
      <div className="map-tint" aria-hidden="true" />
      <button
        className="theme-toggle"
        type="button"
        onClick={onToggleTheme}
        aria-label={COPY.chrome.themeToggle.text}
      >
        {COPY.chrome.themeToggle.text} · {theme}
      </button>
      <LayerTogglePanel activeLayer={activeLayer} onToggle={onToggleLayer} />
      {activeLayer && activeMeasure && (
        <LegendStrip
          activeLayer={activeLayer}
          activeMeasure={activeMeasure}
          onMeasureChange={onChangeMeasure}
        />
      )}
    </div>
  );
}
